import { useState } from "react"
import type { Case, FreightType } from "../../api/types.ts"
import { useT } from "../../i18n/index.tsx"
import { arrivalSave, type ArrivalSave } from "../../lib/arrival-entry.ts"
import { DEFAULT_FREIGHT } from "../../lib/freight.ts"
import { Button, Field, Input, Select } from "../../ui/index.ts"
import { FreightOptions } from "../choice-options.tsx"

/**
 * 방금 찍은 상자에 적는 것 — 중량과 착/선불(착불이면 착불비).
 *
 * 건마다 새로 그린다(`key={case.id}`). 그래서 다음 상자를 찍으면 칸이 저절로 비고,
 * 부모가 "이제 지워라" 를 따로 시킬 필요가 없다.
 *
 * 무엇을 보낼지·저장할 수 있는지는 `lib/arrival-entry.ts` 가 정한다(테스트가 지킨다).
 */
export function ArrivalEntryCard({
  item,
  busy,
  onSave,
  onSkip,
}: {
  item: Case
  busy: boolean
  onSave: (save: ArrivalSave) => void
  onSkip: () => void
}) {
  const t = useT()
  const [weight, setWeight] = useState("")
  // 착불이면 착불비까지 이 자리에서 적는다. 선불이면 착불비 칸을 내지 않는다(서버가 거부한다).
  // 칸은 착불로 서 있는다 — 거의 다 착불로 들어온다(`lib/freight.ts`).
  const [freightType, setFreightType] = useState<FreightType>(DEFAULT_FREIGHT)
  const [codFee, setCodFee] = useState("")
  // 상자를 찍은 직후에는 중량부터 친다. 착불로 바꿔 고른 순간에만 착불비 칸으로 자판을 옮긴다.
  const [codFeeFocus, setCodFeeFocus] = useState(false)

  const save = arrivalSave({ weight, freightType, codFee })

  const saveNow = () => {
    if (save !== null) onSave(save)
  }

  return (
    <div className="scan-weight">
      <p className="scan-weight__who">
        #{item.seq} · {item.orderNo} · {item.customerName}
      </p>
      <div className="scan-weight__row">
        <Field label={t("scan.weightPrompt")} htmlFor="scan-weight">
          <Input
            id="scan-weight"
            // 건마다 새로 그리므로, 새 상자를 찍으면 곧바로 중량을 칠 수 있다.
            autoFocus
            className="scan-input"
            size="lg"
            inputMode="numeric"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveNow()
            }}
          />
        </Field>

        <Field label={t("scan.freight")} htmlFor="scan-freight">
          <Select
            id="scan-freight"
            className="scan-input"
            value={freightType}
            disabled={busy}
            onChange={(event) => {
              const value = event.target.value as FreightType
              setFreightType(value)
              setCodFeeFocus(value === "collect")
              // 선불이면 착불비를 보내지 않으므로 적어 둔 값도 지운다(서버가 거부한다).
              if (value !== "collect") setCodFee("")
            }}
          >
            <FreightOptions />
          </Select>
        </Field>

        {freightType === "collect" ? (
          <Field label={t("case.codFee")} htmlFor="scan-cod-fee" hint={t("scan.codFeeHint")}>
            <Input
              id="scan-cod-fee"
              // 착불로 바꿔 고른 순간에만 자판을 받는다 — 처음에는 중량 칸이 먼저다.
              autoFocus={codFeeFocus}
              aria-describedby="scan-cod-fee-msg"
              className="scan-input"
              size="lg"
              inputMode="numeric"
              value={codFee}
              onChange={(event) => setCodFee(event.target.value.replace(/\D/g, ""))}
              onKeyDown={(event) => {
                if (event.key === "Enter") saveNow()
              }}
            />
          </Field>
        ) : null}

        <Button variant="primary" size="lg" disabled={busy || save === null} onClick={saveNow}>
          {t("scan.save")}
        </Button>
        <Button size="lg" disabled={busy} onClick={onSkip}>
          {t("scan.skipWeight")}
        </Button>
      </div>
    </div>
  )
}
